import { observer } from "mobx-react-lite";
import { useState } from "react";
import { DotsVerticalIcon } from "@heroicons/react/outline";

import { useReportContext } from "../../report-context";

import { HeaderLabel } from "./HeaderLabel";
import { HeaderIndicators } from "./HeaderIndicators";
import { useHeaderContextMenu } from "./hooks/useHeaderContextMenu";

import { ConfirmationModal, ConfirmationModalVariant } from "@/components/ConfirmationModal";
import { ContextMenu } from "@/components/ui/ContextMenu";
import FilterInput from "@/components/ui/table/Filter/FilterInput";
import { ColumnType, ReportColumn } from "@/types/evaluate";
import { cn } from "@/lib/utils";
import { DropdownMenu } from "@/components/ui/DropdownMenu";

interface HeaderCellProps {
  column: ReportColumn;
  reportId: number;
  availableColumns: ReportColumn[];
  isEditable: boolean;
  isDraft?: boolean;
}

const HeaderCell = observer((props: HeaderCellProps) => {
  const {
    openSidebar,
    closeSidebar,
    isSidebarOpen,
    selectedColumn,
    setSelectedColumn: setSidebarColumn,
  } = useReportContext();
  const [showFilterInput, setShowFilterInput] = useState(false);

  const handleToggleSidebar = () => {
    // Don't toggle sidebar if the column type is dataset
    if (props.column.column_type === ColumnType.DATASET) {
      return;
    }

    // Check if this is the same column as the currently selected one
    const isCurrentlySelected = (() => {
      if (!selectedColumn) {
        return false;
      }

      // For regular columns with IDs, compare by ID
      if (
        selectedColumn.id &&
        props.column.id &&
        selectedColumn.id !== -999 &&
        props.column.id !== -999
      ) {
        return selectedColumn.id === props.column.id;
      }

      // For draft columns (no ID or ID === -999), consider them the same if both are drafts
      const isSelectedDraft = !selectedColumn.id || selectedColumn.id === -999;
      const isClickedDraft = !props.column.id || props.column.id === -999;

      return isSelectedDraft && isClickedDraft;
    })();

    if (isSidebarOpen && isCurrentlySelected) {
      closeSidebar();
    } else {
      setSidebarColumn(props.column);
      openSidebar();
    }
  };

  const {
    contextMenuItems,
    isDeleteConfirmationOpen,
    setIsDeleteConfirmationOpen,
    handleConfirmDelete,
    handleCancelDelete,
    actions,
  } = useHeaderContextMenu({
    column: props.column,
    reportId: props.reportId,
    isEditable: props.isEditable,
    showFilterInput,
    setShowFilterInput,
  });

  return (
    <div className="group relative h-[40px] w-full select-none">
      <ContextMenu
        contextMenuItems={contextMenuItems}
        data={props.column}
        shouldOpenContextMenuByLeftClick={props.column.column_type === ColumnType.DATASET}
      >
        <div
          className={cn(
            "relative flex h-full w-full cursor-pointer items-center transition-colors duration-200",
            props.column.id === selectedColumn?.id ? "bg-blue-50" : "",
            props.column.id === -99999999 && "min-w-[250px]",
          )}
          data-column-header
          data-column-id={props.column.id || "-999"}
          data-is-draft={!props.column.id ? "true" : "false"}
          onClick={e => {
            if (props.column.column_type === ColumnType.DATASET) {
              return;
            }
            e.preventDefault();
            e.stopPropagation();
            if (e.button === 0) {
              handleToggleSidebar();
            }
          }}
        >
          <div className="flex h-full w-full items-center transition-all duration-200 group-hover:pr-6">
            <div className="flex w-full items-center justify-between px-2">
              <HeaderLabel column={props.column} />
              <div className="flex items-center gap-x-2">
                <HeaderIndicators column={props.column} isDraft={props.isDraft} />
                <FilterInput
                  column={props.column}
                  isOpen={showFilterInput}
                  setIsOpen={setShowFilterInput}
                />
              </div>
            </div>
          </div>
          {props.column.id && (
            <DropdownMenu
              menuItems={actions}
              item={props.column}
              icon={<DotsVerticalIcon className="h-4 w-4 text-gray-500" />}
              triggerClassName="absolute flex items-center justify-center bg-transparent hover:bg-gray-100 right-2 h-5 w-5 text-gray-500 opacity-0 transition-opacity duration-200 group-hover:opacity-100"
            />
          )}
        </div>
      </ContextMenu>
      <ConfirmationModal
        title="Delete Column"
        description={`Are you sure you want to delete the column "${props.column.name}"? This action cannot be undone.`}
        isOpen={isDeleteConfirmationOpen}
        setIsOpen={setIsDeleteConfirmationOpen}
        leaveButtonText="Cancel"
        saveButtonText="Delete"
        handleOnConfirm={handleConfirmDelete}
        handleLeave={handleCancelDelete}
        handleClose={handleCancelDelete}
        headerVariant={ConfirmationModalVariant.warning}
        saveButtonVariant="destructive"
        exitButtonVariant="secondary"
      />
    </div>
  );
});

export default HeaderCell;
